import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Chip,
  LinearProgress,
  IconButton,
  Grid,
} from "@mui/material";
import {
  EditNote,
  AutoFixHigh,
  ContentCopy,
  Settings,
  Bolt,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import axios from "axios";
import API_BASE_URL from "../config";

const modes = [
  "Standard",
  "Fluency",
  "Formal",
  "Academic",
  "Creative",
  "Shorten",
  "Expand",
];

export default function TextRewriter(): JSX.Element {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("Standard");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const handleRewrite = async () => {
    if (!inputText.trim()) return;
    setLoading(true);

    const prompt = `Rewrite the following text in ${mode} mode while preserving scientific accuracy and academic integrity. Maintain the core message but improve flow and vocabulary: \n\n${inputText}`;

    try {
      const response = await axios.post(`${API_BASE_URL}/gemini-generate`, {
        prompt: prompt,
        model: "gemini-pro",
      }, { timeout: 90000 });

      if (response.data && response.data.result) {
        setOutputText(response.data.result);
      }
    } catch (error) {
      console.error("Rewriting failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: isDarkMode ? "white" : "text.primary",
          }}
        >
          Text{" "}
          <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
            Rewriter
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Professional paraphrasing tool to improve clarity, tone, and academic
          flow.
        </Typography>
      </Box>

      {/* Mode Selection */}
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            p: 0.5,
            borderRadius: "12px",
            bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
            border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
            display: "flex",
            gap: 0.5,
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {modes.map((m) => (
            <Box
              key={m}
              onClick={() => setMode(m)}
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.2s",
                whiteSpace: "nowrap",
                bgcolor:
                  mode === m
                    ? isDarkMode
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(59, 130, 246, 0.1)"
                    : "transparent",
                color:
                  mode === m
                    ? isDarkMode
                      ? "white"
                      : "#1d4ed8"
                    : isDarkMode
                      ? "#6b7280"
                      : "text.secondary",
                "&:hover": {
                  bgcolor:
                    mode === m
                      ? isDarkMode
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(59, 130, 246, 0.15)"
                      : isDarkMode
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.04)",
                  color:
                    mode === m
                      ? isDarkMode
                        ? "white"
                        : "#1d4ed8"
                      : isDarkMode
                        ? "white"
                        : "text.primary",
                },
              }}
            >
              {m}
            </Box>
          ))}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#030303" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              height: 480,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 3, flexGrow: 1 }}>
              <TextField
                fullWidth
                multiline
                placeholder="Paste your text to rewrite..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontFamily: "inherit",
                    fontSize: "15px",
                    lineHeight: 1.6,
                    color: isDarkMode ? "white" : "text.primary",
                    height: "100%",
                    alignItems: "flex-start",
                    "& textarea": { height: "100% !important" },
                  },
                }}
                sx={{ height: "100%" }}
              />
            </Box>
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#4b5563" : "text.secondary" }}
              >
                {inputText.split(/\s+/).filter((x) => x).length} words
              </Typography>
              <Button
                variant="contained"
                onClick={handleRewrite}
                disabled={loading || !inputText.trim()}
                startIcon={<Bolt />}
                sx={{
                  borderRadius: "10px",
                  bgcolor: isDarkMode ? "white" : "primary.main",
                  color: isDarkMode ? "black" : "white",
                  textTransform: "none",
                  px: 4,
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                Rewrite Now
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "24px",
              bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              height: 480,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                }}
              >
                <LinearProgress
                  sx={{
                    height: 2,
                    bgcolor: "transparent",
                    "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" },
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? "#9ca3af" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AutoFixHigh fontSize="small" sx={{ color: "#3b82f6" }} />{" "}
                Output
              </Typography>
              <IconButton
                onClick={handleCopy}
                size="small"
                disabled={!outputText}
                sx={{ color: isDarkMode ? "#6b7280" : "text.secondary" }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  lineHeight: 1.6,
                  color: outputText
                    ? isDarkMode
                      ? "white"
                      : "text.primary"
                    : isDarkMode
                      ? "#4b5563"
                      : "text.secondary",
                  whiteSpace: "pre-wrap",
                }}
              >
                {outputText || "Rewritten text will appear here..."}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
